import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import OsScroll from '../ui/OsScroll';
import PaletteRow from './PaletteRow';
import usePaletteWindow from './usePaletteWindow';
import { itemHeight } from '../../palette/rows';

export const LIST_MAX_HEIGHT = 440;
// OverlayScrollbars re-measures on every size change; while results stream in
// a keystroke at a time, coalesce those measurements (the list is ours to size).
const OS_OPTIONS = { update: { debounce: [40, 120] } };

/** A DOM id for an item key (stable across renders; used by aria-activedescendant). */
export function optionId(listId, key) {
  return `${listId}-o-${String(key).replace(/[^A-Za-z0-9_-]/g, (c) => `_${c.charCodeAt(0).toString(36)}`)}`;
}

/**
 * The virtualised results (PLAN §3.1, D7): only the rows in view plus the
 * overscan are in the DOM (≤ 40 at any time), in the app's OsScroll
 * (owner, 14.10.2026: no native scrollbars). `role="listbox"`, grouped by
 * `role="group"` labelled by its header (§2.7).
 */
export default function PaletteList({ listId, items, activeKey, onHover, onRun, onContext, onForget, t, labelledBy }) {
  const osRef = useRef(null);
  const hostRef = useRef(null);
  const scrollRef = useRef(0); // the last scrollTop we set or saw — never read back from the DOM (no forced layout)
  const [scrollTop, setScrollTop] = useState(0);
  const height0 = useMemo(() => {
    let t = 0;
    for (const it of items) t += itemHeight(it);
    return t;
  }, [items]);
  const height = Math.min(height0, LIST_MAX_HEIGHT);
  const viewport = height || LIST_MAX_HEIGHT;
  const { start, end, total, offsets } = usePaletteWindow(items, scrollTop, viewport);

  const viewportEl = useCallback(() => {
    const inst = osRef.current?.osInstance?.();
    return inst?.elements?.().viewport || hostRef.current?.querySelector('[data-overlayscrollbars-viewport]') || null;
  }, []);

  const scrollTo = useCallback(
    (next) => {
      if (scrollRef.current === next) return;
      scrollRef.current = next;
      const vp = viewportEl();
      if (vp) vp.scrollTop = next;
      setScrollTop(next);
    },
    [viewportEl]
  );

  // Back to the top when the result set is a new one (a new query).
  const firstKey = items[0]?.key;
  useLayoutEffect(() => {
    scrollTo(0);
  }, [firstKey, scrollTo]);

  // Keep the active row in view (keyboard navigation).
  useEffect(() => {
    if (!activeKey) return;
    const i = items.findIndex((it) => it.key === activeKey);
    if (i < 0) return;
    const top = offsets[i];
    const bottom = offsets[i + 1];
    // A row right under its header brings the header along.
    const headerTop = i > 0 && items[i - 1].type === 'header' ? offsets[i - 1] : top;
    const cur = scrollRef.current;
    if (headerTop < cur) scrollTo(headerTop);
    else if (bottom > cur + viewport) scrollTo(bottom - viewport);
  }, [activeKey, items, offsets, viewport, scrollTo]);

  const events = useMemo(
    () => ({
      scroll: (instance) => {
        const vp = instance?.elements?.().viewport;
        if (!vp) return;
        scrollRef.current = vp.scrollTop;
        setScrollTop(vp.scrollTop);
      },
    }),
    []
  );
  const style = useMemo(() => ({ height }), [height]);

  // Group the visible slice for role="group".
  const visible = [];
  let group = null;
  for (let i = start; i < end; i += 1) {
    const item = items[i];
    const rowStyle = { position: 'absolute', top: offsets[i], left: 0, right: 0, height: offsets[i + 1] - offsets[i] };
    const node = (
      <PaletteRow
        key={item.key}
        item={item}
        id={item.type === 'header' ? `${listId}-h-${item.groupId}` : optionId(listId, item.key)}
        active={item.key === activeKey}
        style={rowStyle}
        t={t}
        onHover={item.type === 'header' ? undefined : () => onHover(item.key)}
        onRun={() => onRun(item)}
        onContext={() => onContext(item)}
        onForget={onForget}
      />
    );
    if (item.type === 'header' || !group) {
      const gid = item.type === 'header' ? item.groupId : items.slice(0, i).reverse().find((x) => x.type === 'header')?.groupId || 'none';
      group = { gid, nodes: [] };
      visible.push(group);
    }
    group.nodes.push(node);
  }

  return (
    <div ref={hostRef} className="palette-list-host" style={style}>
      <OsScroll className="palette-scroll command-palette-scroll" ref={osRef} style={style} defer={false} events={events} options={OS_OPTIONS}>
        <div id={listId} role="listbox" aria-labelledby={labelledBy} className="palette-listbox" style={{ position: 'relative', height: total }}>
          {visible.map((g, gi) => (
            <div key={`${g.gid}-${gi}`} role="group" aria-labelledby={`${listId}-h-${g.gid}`}>
              {g.nodes}
            </div>
          ))}
        </div>
      </OsScroll>
    </div>
  );
}
